import React from "react";

import {
  Avatar,
  Box,
  FormControlLabel,
  Radio,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { colors } from "@/theme/overrides/colors";
import { FragmentType } from "@/utils/type";

type SearchAppointmentListItemProps = {
  useRadio: boolean;
  onSelect: () => void;
  searchDocument: FragmentType<typeof searchAppointmentListItemFragment>;
};

const useStyles = makeStyles()({
  container: {
    display: "flex",
    alignItems: "center",
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 10,
    paddingBottom: 10,
    "&:hover": {
      backgroundColor: "#F5F5F5",
      cursor: "pointer",
    },
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 50,
    objectFit: "cover",
  },
  infoContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    marginLeft: 10,
  },
  name: {
    fontSize: 14,
    fontWeight: 600,
  },
  email: {
    color: colors.gray,
  },
});

const searchAppointmentListItemFragment = graphql(`
  fragment SearchAppointmentListItem on SearchAppointmentDocument {
    clientId
    searchableId
    searchableType
    content
    searchable {
      id
      ... on Account {
        email
        owner {
          id
          profile {
            id
            fullName
            avatar {
              id
              url
            }
          }
        }
      }
      ... on Profile {
        fullName
        avatar {
          id
          url
        }
      }
      ... on Appointment {
        title
        client {
          id
          profile {
            fullName
            avatar {
              id
              url
            }
          }
        }
      }
    }
  }
`);

const SearchAppointmentListItem: React.FC<SearchAppointmentListItemProps> = ({
  searchDocument,
  useRadio,
  onSelect,
}) => {
  const { classes } = useStyles();

  const isAppointmentType = searchDocument.searchableType == "Appointment";

  const content = isAppointmentType
    ? `"${searchDocument.content}"`
    : searchDocument.content;

  const _avatarUrl = (): string => {
    switch (searchDocument.searchable.__typename) {
      case "Appointment":
        return searchDocument.searchable["client"].profile?.avatar?.url ?? "";
      case "Account":
        return searchDocument.searchable["owner"].profile?.avatar?.url ?? "";
      case "Profile":
        return searchDocument.searchable["avatar"].url;

      default:
        return "";
    }
  };

  const _fullName = (): string => {
    switch (searchDocument.searchable.__typename) {
      case "Appointment":
        return (
          searchDocument.searchable["client"].profile?.fullName ?? "avatar"
        );
      case "Account":
        return searchDocument.searchable["owner"].profile?.fullName ?? "avatar";
      case "Profile":
        return searchDocument.searchable["fullName"];
      default:
        return "avatar";
    }
  };

  const _email = (): string => {
    switch (searchDocument.searchable.__typename) {
      case "Appointment":
        return searchDocument.searchable["client"].profile?.fullName ?? "";
      case "Account":
        return searchDocument.searchable["email"];
      case "Profile":
        return searchDocument.searchable["fullName"];
      default:
        return "avatar";
    }
  };

  return (
    <Box className={classes.container} onClick={onSelect}>
      <Avatar alt={_fullName()} src={_avatarUrl()} />
      <Box className={classes.infoContainer}>
        <Typography variant="body2" className={classes.name}>
          {content}
        </Typography>
        <Typography variant="caption" className={classes.email}>
          {_email()}
        </Typography>
      </Box>
      {useRadio && (
        <FormControlLabel
          value={searchDocument.clientId}
          control={<Radio color="primary" />}
          label=""
        />
      )}
    </Box>
  );
};

export default SearchAppointmentListItem;
